import { useTranslation } from "react-i18next";
import { useProfileForm } from "../hooks/useProfileForm";
import { useProfile } from "../services/GetProfile";
import Spinner from "@components/common/Spinner";
import ProfileActionButtons from "./ProfileActionButtons";
import ProfileDisplay from "./ProfileDisplay";
import ProfileFormFields from "./ProfileFormFields";
import ProfilePicture from "./ProfilePicture";
import type { ProfileModalTypes } from "../types";

const ProfileModal: React.FC<ProfileModalTypes> = () => {
	const { t } = useTranslation();
	const { data, isLoading } = useProfile();
	const {
		isEditMode,
		isUpdating,
		isUploading,
		form,
		handleImageChange,
		onSubmit,
		handleCancel,
		handleEdit,
		sectorOptions,
		employerTypeOptions,
	} = useProfileForm(data);

	const { control, handleSubmit } = form;

	const displayName =
		data?.businessDetails?.person?.fullName || data?.businessDetails?.businessName || "User";

	if (isLoading) return <Spinner overlay />;

	return (
		<div className="flex flex-1 flex-col">
			<form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col">
				<div className="pb-6">
					<div className="flex flex-col items-center mb-8">
						<ProfilePicture
							profilePicture={data?.profilePicture}
							isUploading={isUploading}
							onImageChange={handleImageChange}
						/>
						<h3
							className="text-xl font-semibold text-foreground text-center max-w-full"
							title={displayName}
							style={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								display: "-webkit-box",
								WebkitLineClamp: 2,
								WebkitBoxOrient: "vertical",
								overflowWrap: "anywhere",
								wordBreak: "break-word",
							}}>
							{displayName}
						</h3>
						{data?.businessDetails?.businessName && data?.businessDetails?.person?.fullName && (
							<p
								className="text-sm text-gray-500 text-center max-w-full"
								title={data?.businessDetails?.businessName}
								style={{
									overflow: "hidden",
									textOverflow: "ellipsis",
									display: "-webkit-box",
									WebkitLineClamp: 2,
									WebkitBoxOrient: "vertical",
									overflowWrap: "anywhere",
									wordBreak: "break-word",
								}}>
								{data?.businessDetails?.businessName}
							</p>
						)}
					</div>

					<div className="w-full space-y-6">
						{isEditMode ? (
							<ProfileFormFields control={control} sectors={sectorOptions} employerTypes={employerTypeOptions} />
						) : (
							<ProfileDisplay businessDetails={data?.businessDetails} />
						)}
					</div>
				</div>

				<ProfileActionButtons
					isEditMode={isEditMode}
					isUpdating={isUpdating}
					onEdit={handleEdit}
					onCancel={handleCancel}
					t={t}
				/>
			</form>
		</div>
	);
};

export default ProfileModal;
